import { Link } from 'react-router-dom'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { favoritesApi } from '../../api/favorites'
import { getStorageUrl } from '../../utils/constants'
import StarRating from '../common/StarRating'
import './CourseCard.css'

const CourseCard = ({ course, viewMode = 'grid' }) => {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const [isFavorited, setIsFavorited] = useState(course.is_favorited || false)
  const [favoriteId, setFavoriteId] = useState(null)

  // Get favorite ID from favorites list
  const { data: favoritesData } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => favoritesApi.getFavorites(),
    enabled: isAuthenticated,
    onSuccess: (data) => {
      if (data?.data?.favorites) {
        const favorite = data.data.favorites.find(
          fav => {
            // Handle polymorphic relationship - check both favoritable object and IDs
            const favoritableType = fav.favoritable_type || 
                                   (fav.favoritable && typeof fav.favoritable === 'object' && 'id' in fav.favoritable ? 'App\\Models\\Course' : null)
            const favoritableId = fav.favoritable_id || fav.favoritable?.id
            
            return (favoritableType === 'App\\Models\\Course' || favoritableType === 'Course') && 
                   favoritableId === course.id
          }
        )
        if (favorite) {
          setFavoriteId(favorite.id)
          setIsFavorited(true)
        } else {
          setIsFavorited(false)
          setFavoriteId(null)
        }
      }
    },
  })

  const favoriteMutation = useMutation({
    mutationFn: async () => {
      if (isFavorited) {
        // Remove favorite
        if (favoriteId) {
          return favoritesApi.removeFavorite(favoriteId)
        } else {
          // If we don't have favoriteId, fetch favorites to find it
          try {
            const favoritesResponse = await favoritesApi.getFavorites()
            const favorites = favoritesResponse?.data?.favorites || []
            const favorite = favorites.find(
              fav => {
                // Handle both string and object formats
                const favoritableType = typeof fav.favoritable_type === 'string' 
                  ? fav.favoritable_type 
                  : fav.favoritable?.constructor?.name || ''
                const favoritableId = fav.favoritable_id || fav.favoritable?.id
                
                return (favoritableType === 'App\\Models\\Course' || favoritableType === 'Course') && 
                       favoritableId === course.id
              }
            )
            if (favorite) {
              return favoritesApi.removeFavorite(favorite.id)
            }
            throw new Error('Favorite ID not found')
          } catch (error) {
            console.error('Error fetching favorites:', error)
            throw error
          }
        }
      } else {
        // Add favorite
        return favoritesApi.addFavorite('course', course.id)
      }
    },
    onSuccess: () => {
      setIsFavorited(!isFavorited)
      setFavoriteId(null)
      queryClient.invalidateQueries(['favorites'])
    },
    onError: (error) => {
      console.error('Favorite error:', error)
      // Don't revert state - let the user see the error
    },
  })

  const handleFavoriteClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isAuthenticated) {
      alert('يجب تسجيل الدخول لإضافة إلى المفضلة')
      return
    }
    favoriteMutation.mutate()
  }

  return (
    <Link
      to={`/courses/${course.id}`}
      className={`group flex flex-col h-full course-card-${viewMode}`}
    >
      {/* Portrait Image Area */}
      <div className="relative w-full rounded-2xl overflow-hidden" style={{ aspectRatio: '0.95' }}>
        {/* Image */}
        <img
          src={getStorageUrl(course.thumbnail) || '/placeholder-course.jpg'}
          alt={course.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20" />
        
        {/* Subject Badge - Top Right */}
        {course.subject?.name && (
          <div className="absolute top-3 right-3 bg-blue-500/85 px-2.5 py-1 rounded-lg">
            <span className="text-white text-[11px] font-bold">
              {course.subject.name}
            </span>
          </div>
        )}
        
        {/* Favorite Button - Top Left */}
        {isAuthenticated && (
          <button
            className="absolute top-3 left-3 w-8 h-8 bg-black/30 rounded-xl flex items-center justify-center transition-colors hover:bg-black/40 z-10"
            onClick={handleFavoriteClick}
            title={isFavorited ? 'إزالة من المفضلة' : 'إضافة إلى المفضلة'}
          >
            {isFavorited ? (
              <svg className="w-4.5 h-4.5 text-[#EFA428]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
              </svg>
            ) : (
              <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Details Section */}
      <div className="px-1 pt-2 flex flex-col flex-grow">
        {/* Title */}
        <h3 className="text-sm font-bold text-white mb-1.5 line-clamp-2 leading-tight">
          {course.title}
        </h3>
        
        {/* Instructor Name */}
        <p className="text-xs text-gray-400 font-medium line-clamp-1">
          {course.instructor?.name || 'Instructor'}
        </p>

        {/* Nothing is shown for a course with no reviews: a zero would read
            as a bad score rather than an absent one. */}
        {course.rating > 0 && (
          <div className="course-card-rating">
            <StarRating value={course.rating} size="sm" />
            <span className="course-card-rating-value">
              {Number(course.rating).toFixed(1)}
            </span>
            {course.reviews_count > 0 && (
              <span className="course-card-rating-count">({course.reviews_count})</span>
            )}
          </div>
        )}
      </div>
    </Link>
  )
}

export default CourseCard

